import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ListChecks, AlertTriangle, Flame, Camera } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/kit";
import { AuthImage, PhotoViewer } from "@/components/conference/PhotoCapture";
import TaskPhotoDialog from "@/components/tasks/TaskPhotoDialog";

function TaskItem({ t, onToggle, busy, onView }) {
  return (
    <div className="flex items-start gap-2">
    <button type="button" disabled={busy} onClick={() => onToggle(t)} className="flex-1 text-left flex items-start gap-3 py-3 group" data-testid={`my-task-${t.id}`}>
      <span className="mt-0.5 shrink-0" style={{ color: t.done ? "var(--success)" : "var(--muted)" }} data-testid={`my-task-check-${t.id}`}>{t.done ? <CheckCircle2 size={22} /> : <Circle size={22} className="group-hover:text-[var(--brand)]" />}</span>
      <span className="flex-1 min-w-0">
        <span className={`label block ${t.done ? "line-through muted" : ""}`}>{t.title}</span>
        {t.description && <span className="caption block mt-0.5">{t.description}</span>}
        <span className="flex flex-wrap gap-1.5 mt-1">
          {t.priority === "alta" && !t.done && <span className="badge badge-danger !py-0"><Flame size={11} /> Prioridade</span>}
          {t.overdue && !t.done && <span className="badge badge-warning !py-0"><AlertTriangle size={11} /> Atrasada · {new Date(`${t.due_date}T12:00`).toLocaleDateString("pt-BR")}</span>}
          {t.recurrence !== "once" && <span className="badge badge-muted !py-0">{t.recurrence === "daily" ? "Diária" : "Semanal"}</span>}
          {t.require_photo && !t.done && <span className="badge badge-brand !py-0" data-testid={`my-task-photo-required-${t.id}`}><Camera size={11} /> Exige foto</span>}
        </span>
      </span>
    </button>
    {t.photo_path && <AuthImage path={t.photo_path} className="w-12 h-12 rounded-lg mt-2 shrink-0" onClick={() => onView(t.photo_path)} testid={`my-task-photo-${t.id}`} />}
    </div>
  );
}

export default function MyTasks() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { isMgmt } = useAuth();
  const [photoFor, setPhotoFor] = useState(null);
  const [view, setView] = useState(null);
  const { data } = useQuery({ queryKey: ["my-tasks"], queryFn: () => api("/tasks/mine") });
  const toggle = useMutation({
    mutationFn: ({ t, photo }) => api(`/tasks/${t.id}/done`, { method: "POST", body: { done: !t.done, photo_path: photo || null } }),
    onSuccess: (_, { t }) => { qc.invalidateQueries({ queryKey: ["my-tasks"] }); qc.invalidateQueries({ queryKey: ["tasks"] }); setPhotoFor(null); if (!t.done) toast.success("Tarefa concluída"); },
    onError: (e) => toast.error(e.message),
  });
  const onToggle = (t) => (t.require_photo && !t.done ? setPhotoFor(t) : toggle.mutate({ t }));
  if (!data || (!data.tasks.length && !isMgmt)) return null;
  const done = data.tasks.filter((t) => t.done).length;
  const pct = data.tasks.length ? Math.round((done / data.tasks.length) * 100) : 0;
  return (
    <Card className="flex flex-col gap-2 mb-5" data-testid="my-tasks-card">
      <div className="flex items-center gap-3">
        <ListChecks size={20} style={{ color: "var(--brand)" }} />
        <div className="flex-1"><div className="h3">Minhas tarefas de hoje</div><div className="caption" data-testid="my-tasks-progress">{data.tasks.length ? `${done} de ${data.tasks.length} concluídas` : "Nenhuma tarefa para você hoje"}</div></div>
        {data.team && <button className="caption underline" onClick={() => nav("/tasks")} data-testid="team-tasks-link">Equipe: {data.team.done}/{data.team.total}</button>}
      </div>
      {data.tasks.length > 0 && <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "var(--surface-alt)" }}><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${pct}%`, background: "var(--brand)" }} /></div>}
      <div className="divide">{data.tasks.map((t) => <TaskItem key={t.id} t={t} busy={toggle.isPending} onToggle={onToggle} onView={setView} />)}</div>
      <TaskPhotoDialog task={photoFor} busy={toggle.isPending} onClose={() => setPhotoFor(null)} onDone={(photo) => toggle.mutate({ t: photoFor, photo })} />
      <PhotoViewer path={view} onClose={() => setView(null)} />
    </Card>
  );
}
